import { createAsyncThunk } from "@reduxjs/toolkit";
import { StoreSearchFilters } from "tsi-common-react/src/apps/retail/models";
import {
    ILocation,
    IRetailStoreWithDistance,
} from "tsi-common-react/src/models/location";
import {
    IProductUUID,
    ISyncStoreID,
} from "tsi-common-react/src/models/nominals";

import {
    getAllFlagshipStorePages,
    getFlagshipStorePage,
    getStoreByExternalID,
    getStores,
} from "./loaders";
import { CMSFlagshipStorePage } from "./models";

interface LoadClosestStoresTempurpedicArgs {
    location: ILocation;
    filters: StoreSearchFilters;
    selectedProduct?: IProductUUID | null;
    loadKey?: string;
}

interface LoadClosestStoresTempurpedicResult {
    stores: ReadonlyArray<IRetailStoreWithDistance>;
    storesWithProduct: ReadonlyArray<IRetailStoreWithDistance>;
    loadKey?: string;
}

export const loadClosestStoresTempurpedic = createAsyncThunk(
    "retail/loadClosestStoresTempurpedic",
    async (
        args: LoadClosestStoresTempurpedicArgs,
    ): Promise<LoadClosestStoresTempurpedicResult> => {
        // Load Stores from multiple sources
        const { allStores, flagshipStores, storesWithProduct } =
            await getStores(args.location, args.filters, args.selectedProduct);

        // Deduplicate and sort stores
        const storesIDs: Set<ISyncStoreID> = new Set();
        const stores = [...allStores, ...flagshipStores, ...storesWithProduct]
            .reduce<IRetailStoreWithDistance[]>((memo, s) => {
                if (!storesIDs.has(s.external_id)) {
                    storesIDs.add(s.external_id);
                    memo.push(s);
                }
                return memo;
            }, [])
            .sort((a, b) => {
                if (a.distance === b.distance) {
                    return 0;
                }
                return a.distance > b.distance ? 1 : -1;
            });

        return {
            stores,
            storesWithProduct,
            loadKey: args.loadKey,
        };
    },
);

interface LoadStoreByExternalIDArgs {
    location: ILocation;
    storeID: ISyncStoreID;
}

export const loadStoreByExternalID = createAsyncThunk(
    "retail/loadStoreByExternalID",
    async (
        args: LoadStoreByExternalIDArgs,
    ): Promise<ReadonlyArray<IRetailStoreWithDistance>> => {
        return await getStoreByExternalID(args.location, args.storeID);
    },
);

export const loadFlagshipStorePage = createAsyncThunk(
    "retail/loadFlagshipStorePage",
    async (storeID: ISyncStoreID): Promise<CMSFlagshipStorePage> => {
        return await getFlagshipStorePage(storeID);
    },
);

export const loadAllFlagshipStorePages = createAsyncThunk(
    "retail/loadAllFlagshipStorePages",
    async (): Promise<CMSFlagshipStorePage[]> => {
        return await getAllFlagshipStorePages();
    },
);
